// Client-side response handlers for every server→client market
// message plus the `error` envelope. Phase 4 Step 19.
//
// Owns the reactive glue: mutates clientState, triggers HUD / panel
// refreshes, clears pending-button flags. main.ts's switch arm
// delegates to `applyMarketServerMessage` for any of the 7 types
// (6 market + error).
//
// Error routing: server populates `payload.context.action` with a tag
// identifying which handler produced the error (Step 19 wire
// commit). Client reads context.action to pick the right pending-
// clear. Errors without an `action` tag (e.g. legacy zone-workshop,
// zone-gather errors) fall through to a clientDebug log and no-op.
//
// Success routing: creditsUpdated.reason encodes the triggering
// action as "<prefix>:<id>" ("listing_purchase:<listingId>",
// "npc_buy:<itemId>", "npc_sell:<itemId>"). Parsed here to clear
// the right pending. Unknown prefixes just update credits.

import { clientDebug } from "../../shared/debug.ts";
import type { CreditsHUD } from "./credits-hud.ts";
import type { InventoryUI } from "./inventory-ui.ts";
import type { ClientMarketState, MarketUI } from "./market-ui.ts";
import type {
  ServerCreditsUpdated,
  ServerError,
  ServerListingCancelled,
  ServerListingCreated,
  ServerListingSold,
  ServerMarketView,
  ServerMessage,
  ServerPriceHistory,
  ServerRemoteMarketView,
} from "./types.ts";

export type MarketClientDeps = {
  readonly clientState: {
    credits: number;
    market: ClientMarketState;
  };
  readonly creditsHud: CreditsHUD;
  readonly marketUI: MarketUI;
  // inventoryUI is reserved for future use (currently handled outside
  // this module via the shared inventoryUpdated path).
  readonly inventoryUI: InventoryUI;
};

export function applyMarketServerMessage(msg: ServerMessage, deps: MarketClientDeps): void {
  switch (msg.type) {
    case "marketView":
      return applyMarketView(msg, deps);
    case "listingCreated":
      return applyListingCreated(msg, deps);
    case "listingCancelled":
      return applyListingCancelled(msg, deps);
    case "listingSold":
      return applyListingSold(msg, deps);
    case "creditsUpdated":
      return applyCreditsUpdated(msg, deps);
    case "priceHistory":
      return applyPriceHistory(msg, deps);
    case "remoteMarketView":
      return applyRemoteMarketView(msg, deps);
    case "error":
      return applyError(msg, deps);
    default:
      // Not a market envelope; caller routes other types elsewhere.
      return;
  }
}

// Full snapshot replace on every poll. priceHistories is client-only
// so preserve it across the replace. Fix 4 (Phase 4 close): the
// payload now carries the caller's server-authoritative credits —
// write through to clientState.credits and refresh the HUD so an
// in-session seller sees a background sale's proceeds at the next
// poll tick instead of waiting for an unrelated creditsUpdated push.
// credits is NOT folded into clientState.market (type keeps the
// shared MarketView shape) — it rides on the wire envelope only.
function applyMarketView(msg: ServerMarketView, deps: MarketClientDeps): void {
  const { credits, ...marketFields } = msg.payload;
  deps.clientState.market = {
    ...marketFields,
    priceHistories: deps.clientState.market.priceHistories,
    remoteHistories: deps.clientState.market.remoteHistories,
  };
  if (deps.clientState.credits !== credits) {
    deps.clientState.credits = credits;
    deps.creditsHud.update(credits);
  }
  deps.marketUI.refresh();
}

// Optimistic add — listings tab re-renders with the new row before
// the next marketView poll reconciles. Seller's credits already
// reflect fee deduction (Flow A).
function applyListingCreated(msg: ServerListingCreated, deps: MarketClientDeps): void {
  const { listing, credits } = msg.payload;
  deps.clientState.market = {
    ...deps.clientState.market,
    listings: [...deps.clientState.market.listings, listing],
  };
  deps.clientState.credits = credits;
  deps.creditsHud.update(credits);
  deps.marketUI.clearListPending();
  deps.marketUI.refresh();
}

// Optimistic remove. Credits unchanged (fee retained); we still push
// the value through for idempotency with the wire shape.
function applyListingCancelled(msg: ServerListingCancelled, deps: MarketClientDeps): void {
  const { listingId, credits } = msg.payload;
  deps.clientState.market = {
    ...deps.clientState.market,
    listings: deps.clientState.market.listings.filter((l) => l.listingId !== listingId),
  };
  deps.clientState.credits = credits;
  deps.creditsHud.update(credits);
  deps.marketUI.clearListingPending(listingId);
  deps.marketUI.refresh();
}

// Phase 5 Step 21 followup — real-time seller notification.
// Step 4 wired the server-side durable push at sale time; Step 19
// retired seller-side marketView polling. This handler is what
// consumes the push: prune the sold listing from local state so the
// seller's listings tab reflects the sale immediately. Credits are
// updated by the paired `creditsUpdated` envelope (reason
// `listing_sale:<listingId>`) — no need to duplicate the write here.
// Toast spawn lives in server-message-reducer.ts (needs
// clientState.party.toasts which isn't in MarketClientDeps).
function applyListingSold(msg: ServerListingSold, deps: MarketClientDeps): void {
  const { listingId } = msg.payload;
  deps.clientState.market = {
    ...deps.clientState.market,
    listings: deps.clientState.market.listings.filter((l) => l.listingId !== listingId),
  };
  deps.marketUI.refresh();
  clientDebug("listingSold applied", msg.payload);
}

// Any credit delta. Reason prefix triggers the matching pending-
// clear; unknown reasons just update the HUD + refresh.
function applyCreditsUpdated(msg: ServerCreditsUpdated, deps: MarketClientDeps): void {
  deps.clientState.credits = msg.payload.credits;
  deps.creditsHud.update(msg.payload.credits);
  const reason = msg.payload.reason;
  const purchasePrefix = "listing_purchase:";
  const npcBuyPrefix = "npc_buy:";
  const npcSellPrefix = "npc_sell:";
  if (reason.startsWith(purchasePrefix)) {
    deps.marketUI.clearListingPending(reason.slice(purchasePrefix.length));
  } else if (reason.startsWith(npcBuyPrefix)) {
    deps.marketUI.clearNpcBuyPending(reason.slice(npcBuyPrefix.length));
  } else if (reason.startsWith(npcSellPrefix)) {
    deps.marketUI.clearNpcSellPending(reason.slice(npcSellPrefix.length));
  }
  deps.marketUI.refresh();
}

// Indexed per-item so multiple in-flight getPriceHistory responses
// don't clobber each other. History tab re-renders if open.
function applyPriceHistory(msg: ServerPriceHistory, deps: MarketClientDeps): void {
  deps.clientState.market = {
    ...deps.clientState.market,
    priceHistories: {
      ...deps.clientState.market.priceHistories,
      [msg.payload.itemId]: msg.payload.entries,
    },
  };
  deps.marketUI.refresh();
}

// Phase 6 Block C: Market Analysis cross-station price history response.
// Stored under remoteHistories[stationId][itemKey] so the History tab can
// display it when a remote station is selected in the picker.
function applyRemoteMarketView(msg: ServerRemoteMarketView, deps: MarketClientDeps): void {
  const { stationId, itemKey, entries } = msg.payload;
  const prev = deps.clientState.market.remoteHistories[stationId] ?? {};
  deps.clientState.market = {
    ...deps.clientState.market,
    remoteHistories: {
      ...deps.clientState.market.remoteHistories,
      [stationId]: { ...prev, [itemKey]: entries },
    },
  };
  deps.marketUI.refresh();
}

// Routes error envelopes to the matching pending-clear based on
// payload.context.action (server Step 19 wire commit). clientDebug
// every error so QA has a trail even when the UX hides it.
function applyError(msg: ServerError, deps: MarketClientDeps): void {
  clientDebug("server error", msg.payload);
  const ctx = msg.payload.context;
  if (!ctx) return;
  const action = typeof ctx["action"] === "string" ? (ctx["action"] as string) : null;
  if (action === null) return;
  if (action === "listItem") {
    deps.marketUI.clearListPending();
    deps.marketUI.refresh();
    return;
  }
  if (action === "cancelListing" || action === "buyListing") {
    const listingId = typeof ctx["listingId"] === "string" ? (ctx["listingId"] as string) : null;
    if (listingId !== null) deps.marketUI.clearListingPending(listingId);
    deps.marketUI.refresh();
    return;
  }
  if (action === "npcBuy") {
    const itemId = typeof ctx["itemId"] === "string" ? (ctx["itemId"] as string) : null;
    if (itemId !== null) deps.marketUI.clearNpcBuyPending(itemId);
    deps.marketUI.refresh();
    return;
  }
  if (action === "npcSell") {
    const itemId = typeof ctx["itemId"] === "string" ? (ctx["itemId"] as string) : null;
    if (itemId !== null) deps.marketUI.clearNpcSellPending(itemId);
    deps.marketUI.refresh();
    return;
  }
  if (action === "repairItem") {
    const rowid = typeof ctx["inventoryRowid"] === "number" ? (ctx["inventoryRowid"] as number) : null;
    if (rowid !== null) deps.marketUI.clearRepairPending(rowid);
    deps.marketUI.refresh();
    return;
  }
  // getMarketView / getPriceHistory errors have no pending to clear.
}
